import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Briefcase, Users, CheckCircle2, XCircle, Calendar, Mail, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_COLORS = {
  new:          "bg-blue-100 text-blue-800",
  shortlisted:  "bg-emerald-100 text-emerald-800",
  interviewing: "bg-amber-100 text-amber-800",
  offered:      "bg-fuchsia-100 text-fuchsia-800",
  rejected:     "bg-stone-200 text-stone-700",
};

export default function Recruiter() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("recruiter");   // recruiter | candidate
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const path = role === "recruiter" ? "/recruiter/applications" : "/recruiter/applications/mine";
      const r = await api.get(path);
      setApps(r.data || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user, role]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/recruiter/applications/${id}/shortlist`, { status });
      toast.success(status.charAt(0).toUpperCase() + status.slice(1));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(""); }
  };

  const schedule = async (id) => {
    const at = window.prompt("Interview time (ISO like 2026-03-01T15:00:00)?");
    if (!at) return;
    setBusyId(id);
    try {
      await api.post(`/recruiter/applications/${id}/schedule`, { at, mode: "video" });
      toast.success("Interview scheduled");
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusyId(""); }
  };

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to view your recruiter dashboard.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-4" data-testid="recruiter-page">
      <div className="flex items-center gap-2 mb-1"><Briefcase className="text-[#E05E36]" size={22} />
        <h1 className="heading text-2xl font-black text-stone-900">Recruiter</h1>
      </div>
      <p className="text-xs text-stone-500 mb-3">Applications inbox for your job listings + interview scheduler.</p>

      <div className="flex gap-2 mb-3">
        {[
          { k: "recruiter", l: "As Recruiter" },
          { k: "candidate", l: "My Applications" },
        ].map(t => (
          <button key={t.k} onClick={() => setRole(t.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${role === t.k ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}
            data-testid={`recruiter-role-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? <div className="text-center py-16"><Loader2 className="inline animate-spin" /></div>
      : apps.length === 0 ? (
        <div className="text-center py-16 text-stone-500">
          <Users className="mx-auto mb-2 text-stone-300" size={32} />
          {role === "recruiter" ? "No applications yet — share your job listings to attract candidates." : "You haven't applied to any jobs yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {apps.map(a => (
            <div key={a.application_id} className="bg-white border border-stone-200 rounded-2xl p-3" data-testid={`recruiter-app-${a.application_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${STATUS_COLORS[a.status] || STATUS_COLORS.new}`}>{a.status}</span>
                <span className="text-[10px] text-stone-500 font-mono">{a.application_id.slice(0, 12)}</span>
              </div>
              <div className="font-bold text-sm text-stone-900">
                {role === "recruiter" ? a.applicant_name : a.listing_title}
              </div>
              <div className="text-xs text-stone-600 line-clamp-2 mt-0.5">{a.cover_note || "(No cover note)"}</div>
              {role === "recruiter" && (
                <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-stone-500">
                  {a.applicant_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {a.applicant_email}</span>}
                  {a.applicant_phone && <a href={`tel:${a.applicant_phone}`} className="inline-flex items-center gap-1"><Phone size={11} /> {a.applicant_phone}</a>}
                </div>
              )}
              {a.interview && (
                <div className="mt-1 text-xs text-fuchsia-700 inline-flex items-center gap-1">
                  <Calendar size={11} /> Interview {a.interview.at} · {a.interview.mode}
                </div>
              )}
              {role === "recruiter" && (
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {a.status !== "shortlisted" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(a.application_id, "shortlisted")} disabled={busyId === a.application_id} className="rounded-full text-xs" data-testid={`app-shortlist-${a.application_id}`}>
                      <CheckCircle2 size={11} className="mr-1 text-emerald-600" /> Shortlist
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => schedule(a.application_id)} disabled={busyId === a.application_id} className="rounded-full text-xs" data-testid={`app-schedule-${a.application_id}`}>
                    <Calendar size={11} className="mr-1 text-fuchsia-600" /> Schedule
                  </Button>
                  {a.status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(a.application_id, "rejected")} disabled={busyId === a.application_id} className="rounded-full text-xs" data-testid={`app-reject-${a.application_id}`}>
                      <XCircle size={11} className="mr-1 text-red-500" /> Reject
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
